import { type ReactNode, useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";

import { BpmnFileField } from "./bpmn-file-field";
import { BpmnPreview } from "./bpmn-preview";
import {
  type CreateErrorView,
  describeCreateError,
} from "./describe-create-error";
import type { SelectedBpmnFile } from "./read-bpmn-file";
import {
  type BpmnFileSelection,
  useBpmnFileSelection,
} from "./use-bpmn-file-selection";
import {
  type CreateModelInput,
  type CreateModelSource,
  useCreateModel,
} from "./use-create-model";
import { type DesignModel, useDesignModels } from "./use-design-models";
import { type ProcessGroup, useProcessGroups } from "./use-process-groups";
import { validateIdentifier } from "./validate-identifier";

export type NewModelFormProps = {
  readonly onCreated: (_processModelId: string) => void;
  readonly onCancel: () => void;
  /** 作成が途中で失敗し、作りかけのモデルが残ったときに開く。 */
  readonly onOpenModel: (_processModelId: string) => void;
};

/**
 * グループ欄で「新しいグループを作る」を表す値。この画面で作るグループ id は
 * 英小文字・数字・ハイフンに限るので、`_` を含むこの値とは重ならない。
 */
const NEW_GROUP = "__new__";

type TextFields = {
  readonly modelName: string;
  readonly displayName: string;
  /**
   * 選んだファイルから自動で入れた欄。利用者が書き換えたら false に戻す。
   * true の欄は、ファイルを選び直せば入れ直し、外せば空に戻す（ファイルに従う）。
   */
  readonly filled: {
    readonly modelName: boolean;
    readonly displayName: boolean;
  };
};

const EMPTY_FIELDS: TextFields = {
  modelName: "",
  displayName: "",
  filled: { modelName: false, displayName: false },
};

const ISSUE_KEYS = {
  invalid: "processDesign.newModel.invalidId",
  duplicate: "processDesign.newModel.duplicateId",
} as const;

/**
 * モデルの新規作成フォーム。作成中だけ、設計画面の右ペインに出す。
 *
 * 並びは 作り方 → ファイル → グループ → モデル名 → 表示名。ファイルを先に選べば、
 * 空のモデル名と表示名をファイルから埋められる。
 *
 * 件名の変数名は**聞かない**。一覧の件名とキーワード検索は全モデル共通の予約キー
 * （`case_title`）だけを見るので、利用者に選ばせると「件名が出ないモデル」を
 * 作れてしまう。予約キーの宣言は作成時に必ず入る（use-create-model）。
 */
export function NewModelForm({
  onCreated,
  onCancel,
  onOpenModel,
}: NewModelFormProps) {
  const { t } = useTranslation("common");
  const create = useCreateModel();
  const groupsQuery = useProcessGroups();
  const modelsQuery = useDesignModels();
  const selection = useBpmnFileSelection();
  const sourceName = useId();

  const [source, setSource] = useState<"starter" | "file">("starter");
  /** null は未操作（既定の選択に従う）。 */
  const [group, setGroup] = useState<string | null>(null);
  const [newGroupId, setNewGroupId] = useState("");
  const [newGroupName, setNewGroupName] = useState("");
  const [fields, setFields] = useState<TextFields>(EMPTY_FIELDS);

  const groups = mergeGroups(groupsQuery.data ?? [], modelsQuery.data ?? []);
  const modelIds = (modelsQuery.data ?? []).map(
    (model) => model.processModelId,
  );

  // グループが 1 つも無い環境では、選べるものが無いので最初から新しく作る
  const effectiveGroup =
    group ?? (groupsQuery.isSuccess && groups.length === 0 ? NEW_GROUP : "");
  const isNewGroup = effectiveGroup === NEW_GROUP;
  const groupId = isNewGroup ? newGroupId.trim() : effectiveGroup;

  const newGroupIssue = isNewGroup
    ? identifierIssue(
        newGroupId,
        groups.some((candidate) => candidate.id === newGroupId.trim()),
      )
    : null;
  const modelIssue = identifierIssue(
    fields.modelName,
    groupId !== "" &&
      modelIds.includes(`${groupId}/${fields.modelName.trim()}`),
  );

  // 作り方と選んだファイルから、送る中身を 1 回だけ決める（判定と送信で食い違わない）
  const createSource = createSourceOf(source, selection);

  const isGroupReady = isNewGroup
    ? validateIdentifier(newGroupId) === "ok" &&
      newGroupIssue === null &&
      newGroupName.trim() !== ""
    : effectiveGroup !== "";

  const canSubmit =
    createSource !== null &&
    isGroupReady &&
    validateIdentifier(fields.modelName) === "ok" &&
    modelIssue === null &&
    fields.displayName.trim() !== "" &&
    !create.isPending;

  /** ファイルから入れた欄を空に戻す（ファイルを外した・雛形に戻したとき）。 */
  const dropFilledFields = () => {
    setFields((current) => ({
      modelName: current.filled.modelName ? "" : current.modelName,
      displayName: current.filled.displayName ? "" : current.displayName,
      filled: { modelName: false, displayName: false },
    }));
  };

  const selectFile = (picked: File) => {
    void selection.select(picked).then((result) => {
      // null は、読み終わる前に別のファイルを選んだか外した（古い結果は使わない）
      if (result === null) return;
      setFields((current) => fillFromFile(current, result));
    });
  };

  const submit = () => {
    if (!canSubmit || createSource === null) return;

    const input: CreateModelInput = {
      group: groupId,
      modelName: fields.modelName.trim(),
      displayName: fields.displayName.trim(),
      source: createSource,
      ...(isNewGroup ? { newGroup: { displayName: newGroupName.trim() } } : {}),
    };

    create.mutate(input, {
      onSuccess: (result) => onCreated(result.processModelId),
      onError: (error) => {
        // グループを作ったあとで失敗した場合は、再試行で二重に作らないよう既存扱いに切り替える
        const created = describeCreateError(error).createdGroup;
        if (created === null) return;
        setGroup(created.id);
        setNewGroupId("");
        setNewGroupName("");
      },
    });
  };

  return (
    <form
      className="border-border flex flex-col gap-4 rounded-md border p-4"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <h2 className="text-base font-semibold">
        {t("processDesign.newModel.title")}
      </h2>

      <fieldset className="m-0 flex flex-col gap-1 border-0 p-0 text-sm">
        <legend className="mb-1 p-0 font-medium">
          {t("processDesign.newModel.source")}
        </legend>
        {(["starter", "file"] as const).map((value) => (
          <label key={value} className="flex items-center gap-2">
            <input
              type="radio"
              name={sourceName}
              value={value}
              checked={source === value}
              onChange={() => {
                setSource(value);
                // 雛形に戻したら、ファイルとそこから入れた値はもう当てはまらない
                // （読み込み中のファイルが後から欄を埋めないよう、選択ごと外す）
                if (value === "starter") {
                  selection.clear();
                  dropFilledFields();
                }
              }}
            />
            {t(
              value === "starter"
                ? "processDesign.newModel.sourceStarter"
                : "processDesign.newModel.sourceFile",
            )}
          </label>
        ))}
      </fieldset>

      {source === "file" ? (
        <div className="flex flex-col gap-2">
          <BpmnFileField
            selected={selection.file}
            onSelect={selectFile}
            onClear={() => {
              selection.clear();
              dropFilledFields();
            }}
          />
          {selection.ready === null ? null : (
            <>
              <p className="text-muted-foreground m-0 text-xs">
                {t("processDesign.primaryProcess")}:{" "}
                <span className="font-mono">
                  {selection.ready.meta.processId}
                </span>
              </p>
              {selection.previewStatus === "failed" ? (
                <p role="alert" className="text-destructive m-0 text-xs">
                  {t("processDesign.newModel.previewFailed")}
                </p>
              ) : null}
              <BpmnPreview
                // 選ぶたびに作り直す（一度描けなかった部品は描き直さないため）
                key={selection.version}
                xml={selection.ready.xml}
                label={t("processDesign.bpmnFile.preview")}
                onRendered={selection.markPreviewRendered}
                onRenderError={selection.markPreviewFailed}
              />
            </>
          )}
        </div>
      ) : null}

      <GroupField
        groups={groups}
        isListUnavailable={groupsQuery.isError}
        value={effectiveGroup}
        onChange={setGroup}
        newGroupId={newGroupId}
        onNewGroupIdChange={setNewGroupId}
        newGroupName={newGroupName}
        onNewGroupNameChange={setNewGroupName}
        newGroupIssue={
          newGroupIssue === null ? null : t(ISSUE_KEYS[newGroupIssue])
        }
      />

      <Field
        label={t("processDesign.newModel.modelName")}
        hint={t("processDesign.newModel.modelNameHint")}
        value={fields.modelName}
        onChange={(modelName) =>
          setFields((current) => ({
            ...current,
            modelName,
            filled: { ...current.filled, modelName: false },
          }))
        }
        note={
          fields.filled.modelName
            ? t("processDesign.newModel.filledFromFileName")
            : undefined
        }
        issue={modelIssue === null ? undefined : t(ISSUE_KEYS[modelIssue])}
      />
      <Field
        label={t("processDesign.newModel.displayName")}
        value={fields.displayName}
        onChange={(displayName) =>
          setFields((current) => ({
            ...current,
            displayName,
            filled: { ...current.filled, displayName: false },
          }))
        }
        note={
          fields.filled.displayName
            ? t("processDesign.newModel.filledFromProcessName")
            : undefined
        }
      />

      <p className="text-muted-foreground m-0 text-xs">
        {t(
          source === "starter"
            ? "processDesign.newModel.note"
            : "processDesign.newModel.noteFile",
        )}
      </p>

      {create.isError && create.variables !== undefined ? (
        <CreateError
          view={describeCreateError(create.error)}
          // 文言の id は送信したときの値を使う（あとで欄を変えても書き換わらない）
          submittedGroup={create.variables.group}
          onOpenModel={onOpenModel}
        />
      ) : null}

      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={!canSubmit}>
          {t("processDesign.newModel.submit")}
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={onCancel}>
          {t("processDesign.newModel.cancel")}
        </Button>
      </div>
    </form>
  );
}

/**
 * 送る中身。ファイルから作る指定で、使えるファイルが無い・図を描けたと分かって
 * いない（描画中・描けない）ときは null（作成させない）。
 */
function createSourceOf(
  source: "starter" | "file",
  selection: BpmnFileSelection,
): CreateModelSource | null {
  if (source === "starter") return { kind: "starter" };
  if (selection.ready === null || selection.previewStatus !== "ok") return null;
  return {
    kind: "file",
    xml: selection.ready.xml,
    processId: selection.ready.meta.processId,
  };
}

/**
 * 選んだファイルから欄を埋める。空の欄と、前のファイルから入れた欄だけを対象にし、
 * 利用者が書いた値は上書きしない。モデル名は id になり後から変えられないので、
 * ファイル名を変換してまで埋めない（規則にそのまま合うときだけ）。
 */
function fillFromFile(current: TextFields, file: SelectedBpmnFile): TextFields {
  const stem = file.fileName.replace(/\.bpmn$/i, "");
  const processName = file.meta.kind === "ok" ? file.meta.processName : "";
  const canFillName =
    current.filled.modelName || current.modelName.trim() === "";
  const canFillDisplay =
    current.filled.displayName || current.displayName.trim() === "";
  const name =
    file.meta.kind === "ok" && validateIdentifier(stem) === "ok" ? stem : "";

  return {
    modelName: canFillName ? name : current.modelName,
    displayName: canFillDisplay ? processName : current.displayName,
    filled: {
      modelName: canFillName && name !== "",
      displayName: canFillDisplay && processName !== "",
    },
  };
}

/** 入力前（空）は理由を出さない。空は「まだ書いていない」だけ。 */
function identifierIssue(
  value: string,
  exists: boolean,
): keyof typeof ISSUE_KEYS | null {
  const result = validateIdentifier(value);
  if (result === "empty") return null;
  if (result === "invalid") return "invalid";
  return exists ? "duplicate" : null;
}

/**
 * 選べるグループ。グループ一覧 API を正とし、取れなかったときに備えて
 * モデル一覧の所属グループも混ぜる（一覧が出ないと既存グループに作れなくなる）。
 */
function mergeGroups(
  groups: readonly ProcessGroup[],
  models: readonly DesignModel[],
): readonly ProcessGroup[] {
  const byId = new Map(groups.map((group) => [group.id, group]));
  for (const model of models) {
    const segments = model.processModelId.split("/").slice(0, -1);
    segments.forEach((segment, index) => {
      const id = segments.slice(0, index + 1).join("/");
      if (byId.has(id)) return;
      byId.set(id, {
        id,
        displayName: model.groupPath[index]?.displayName ?? segment,
      });
    });
  }
  return [...byId.values()].sort((a, b) => (a.id < b.id ? -1 : 1));
}

function GroupField({
  groups,
  isListUnavailable,
  value,
  onChange,
  newGroupId,
  onNewGroupIdChange,
  newGroupName,
  onNewGroupNameChange,
  newGroupIssue,
}: {
  readonly groups: readonly ProcessGroup[];
  /** グループ一覧 API が失敗した（モデルの無いグループが候補に出ない）。 */
  readonly isListUnavailable: boolean;
  readonly value: string;
  readonly onChange: (_value: string) => void;
  readonly newGroupId: string;
  readonly onNewGroupIdChange: (_value: string) => void;
  readonly newGroupName: string;
  readonly onNewGroupNameChange: (_value: string) => void;
  readonly newGroupIssue: string | null;
}) {
  const { t } = useTranslation("common");
  const id = useId();

  return (
    <div className="flex flex-col gap-1 text-sm">
      <label htmlFor={id} className="font-medium">
        {t("processDesign.newModel.group")}
      </label>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="border-input bg-background rounded-md border px-2 py-1.5 text-sm"
      >
        <option value="" disabled>
          {t("processDesign.newModel.groupPlaceholder")}
        </option>
        {groups.map((group) => (
          <option key={group.id} value={group.id}>
            {t("processDesign.newModel.groupOption", {
              id: group.id,
              name: group.displayName,
            })}
          </option>
        ))}
        <option value={NEW_GROUP}>
          {t("processDesign.newModel.groupNew")}
        </option>
      </select>
      {isListUnavailable ? (
        <p className="text-muted-foreground m-0 text-xs">
          {t("processDesign.newModel.groupsUnavailable")}
        </p>
      ) : null}
      {value === NEW_GROUP ? (
        <div className="border-border mt-1 flex flex-col gap-3 border-l-2 pl-3">
          <Field
            label={t("processDesign.newModel.groupId")}
            hint={t("processDesign.newModel.groupIdHint")}
            value={newGroupId}
            onChange={onNewGroupIdChange}
            issue={newGroupIssue ?? undefined}
          />
          <Field
            label={t("processDesign.newModel.groupDisplayName")}
            value={newGroupName}
            onChange={onNewGroupNameChange}
          />
        </div>
      ) : null}
    </div>
  );
}

function CreateError({
  view,
  submittedGroup,
  onOpenModel,
}: {
  readonly view: CreateErrorView;
  readonly submittedGroup: string;
  readonly onOpenModel: (_processModelId: string) => void;
}) {
  const { t } = useTranslation("common");
  const { model } = view;

  const lines: ReactNode[] = [];
  if (view.createdGroup !== null) {
    lines.push(
      t("processDesign.newModel.groupKept", {
        name: view.createdGroup.displayName,
        id: view.createdGroup.id,
      }),
    );
  }
  if (model.kind === "rolled-back") {
    lines.push(t("processDesign.newModel.rolledBack"));
  }
  if (model.kind === "left-over") {
    lines.push(t("processDesign.newModel.leftover", { id: model.id }));
  }
  // 理由を特定できない失敗は、モデルを作れていない場合だけ「作成できませんでした」と出す
  // （削除して戻した・残った場合は上の行で足りる）
  if (view.reason !== "failed" || model.kind === "not-created") {
    lines.push(reasonLine(view, submittedGroup, t));
  }
  // 押し直しても通らない失敗（重複・権限・不正な BPMN）には促さない
  if (view.retryable && model.kind !== "left-over") {
    lines.push(t("processDesign.newModel.retry"));
  }

  return (
    <div role="alert" className="text-destructive flex flex-col gap-1 text-xs">
      {lines.map((line, index) => (
        <p key={index} className="m-0">
          {line}
        </p>
      ))}
      {model.kind === "left-over" ? (
        <div>
          <Button
            type="button"
            size="xs"
            variant="outline"
            onClick={() => onOpenModel(model.id)}
          >
            {t("processDesign.newModel.openLeftover")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function reasonLine(
  view: CreateErrorView,
  submittedGroup: string,
  t: ReturnType<typeof useTranslation<"common">>["t"],
): string {
  switch (view.reason) {
    case "duplicate":
      return view.stage === "group"
        ? t("processDesign.newModel.failedGroupDuplicate", {
            id: submittedGroup,
          })
        : t("processDesign.newModel.failedDuplicate");
    case "forbidden":
      return t("processDesign.newModel.failedForbidden");
    case "group-missing":
      return t("processDesign.newModel.failedGroupMissing", {
        id: submittedGroup,
      });
    case "file-invalid":
      return t("processDesign.newModel.failedFileInvalid", {
        detail: view.detail ?? "",
      });
    case "failed":
      return t("processDesign.newModel.failed");
  }
}

/**
 * ヒントは label の外に出す。label の中に入れるとラベル名にヒントまで含まれ、
 * 支援技術が「モデル名 英小文字・数字・ハイフン…」と読み上げてしまう。
 */
function Field({
  label,
  hint,
  note,
  issue,
  value,
  onChange,
}: {
  readonly label: string;
  readonly hint?: string;
  /** 自動で入れたことの断り書きなど。 */
  readonly note?: string | undefined;
  readonly issue?: string | undefined;
  readonly value: string;
  readonly onChange: (_value: string) => void;
}) {
  const id = useId();
  const hintId = `${id}-hint`;
  const issueId = `${id}-issue`;
  const describedBy = [
    hint === undefined ? null : hintId,
    issue === undefined ? null : issueId,
  ]
    .filter((part) => part !== null)
    .join(" ");

  return (
    <div className="flex flex-col gap-1 text-sm">
      <label htmlFor={id} className="font-medium">
        {label}
      </label>
      <input
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        {...(describedBy === "" ? {} : { "aria-describedby": describedBy })}
        className="border-input bg-background rounded-md border px-2 py-1.5 text-sm"
      />
      {hint === undefined ? null : (
        <p id={hintId} className="text-muted-foreground m-0 text-xs">
          {hint}
        </p>
      )}
      {note === undefined ? null : (
        <p className="text-muted-foreground m-0 text-xs">{note}</p>
      )}
      {issue === undefined ? null : (
        <p id={issueId} role="alert" className="text-destructive m-0 text-xs">
          {issue}
        </p>
      )}
    </div>
  );
}
